'use client';

import { genericMemo } from '@/types/use';
import * as React from 'react';
import {
    ArrayPath,
    FieldArrayWithId,
    Path,
    useFieldArray,
} from 'react-hook-form';
import { FieldArrayActions, FormFieldsValues } from './form-types';

type Props<T extends FormFieldsValues, U extends FormFieldsValues> = {
    name: Path<T>;
    className?: string;
    children: (
        fields: FieldArrayWithId<T, ArrayPath<T>, 'id'>[],
        actions: FieldArrayActions<U>
    ) => React.ReactNode;
};

const CustomFormList = <
    T extends FormFieldsValues = {},
    U extends FormFieldsValues = {},
>(
    props: Props<T, U>
) => {
    const { name, children, ...rest } = props;
    const { append, fields, remove, update } = useFieldArray<T>({
        name: name as any,
    });

    const actions = React.useMemo<FieldArrayActions<U>>(() => {
        return {
            add(data) {
                append((data as any) ?? undefined);
            },
            remove(index) {
                remove(index);
            },
            replace() {},
            update(index: number, data?: T) {
                update(index, data as any);
            },
        };
    }, [append, remove, update]);

    return <div {...rest}>{children(fields, actions)}</div>;
};

export default genericMemo(CustomFormList);
